// ─── Ocean Editorial Collage V1 ───────────────────────────────
// Curated editorial collage with strict visual grammar:
//   Hero  — full-bleed ocean bg + centered polaroid + micro labels
//   Story — white editorial canvas + asymmetric photos + typewriter + torn note + East Asian quote
//   Footer — atmospheric beach bg + optional closing text
// Not a random collage. This is curated editorial collage.

export const oceanEditorialCollageV1 = {
  templateId: 'ocean-editorial-collage-v1',
  name: 'Ocean Editorial',
  description: 'Cinematic ocean backdrop, editorial typography, and handwritten notes.',
  category: 'wedding',
  previewColors: ['#f3f0eb', '#6b655e', '#1f1b17'],
  isCollage: true,
  theme: {
    palette: 'warm-paper',
    fontPair: 'cormorant-manrope',
    radius: 'soft',
    shadow: 'soft',
    overlay: 'none',
    themeExtensions: {
      paper: '#f4f1eb',
      paperDark: '#ece7de',
      borderStrong: '#d8cbc0',
      // Custom font stack for this template
      displayFont: '"Cormorant Garamond", serif',
      monoFont: '"Special Elite", "Courier New", monospace',
      handFont: '"Caveat", cursive',
      eastAsianFont: '"Noto Serif SC", serif',
      shadowSoft: '0 20px 44px rgba(0,0,0,0.16)',
      shadowPaper: '0 8px 20px rgba(0,0,0,0.06)',
    },
  },
  settings: {
    musicEnabled: false,
    countdownEnabled: false,
    rsvpEnabled: false,
    showMapLink: false,
  },
  sections: [
    // ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 1 dominant centered polaroid, 3 micro labels, atmospheric ocean bg
    {
      id: 'hero-collage',
      type: 'collage',
      label: 'Hero',
      variant: 'ocean-hero',
      sectionStyle: 'heroPolaroidOverPhotoBackground',
      visible: true,
      height: { mobile: 760, desktop: 860 },
      background: {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1505142468610-359e7d316be0?q=80&w=1600&auto=format&fit=crop',
        overlay: 'rgba(245,241,235,0.22)',
        grayscale: 0.3,
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [
        // ── Central Polaroid ──
        {
          id: 'hero-polaroid-main',
          type: 'image_card',
          style: 'polaroid',
          src: 'https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop',
          caption: 'the first day of forever',
          rotation: 0,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'fixed',
            mobileBehavior: 'preserve',
            overlapPolicy: 'none',
            animationPriority: 1,
          },
          layout: {
            desktop: { x: 0.47, y: 0.39, w: 0.42, z: 10 },
            mobile:  { x: 0.47, y: 0.34, w: 0.74, z: 10 },
          },
        },
        // ── Micro Labels (bottom row, hidden on mobile) ──
        {
          id: 'hero-label-left',
          type: 'text_block',
          style: 'micro-label',
          content: 'poster design\n2026',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.12, y: 0.72, w: 0.13, z: 3 },
            mobile:  { x: 0.14, y: 0.72, w: 0.20, z: 3 },
          },
        },
        {
          id: 'hero-label-center',
          type: 'text_block',
          style: 'micro-label-center',
          content: 'our story',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.50, y: 0.73, w: 0.16, z: 3 },
            mobile:  { x: 0.50, y: 0.73, w: 0.22, z: 3 },
          },
        },
        {
          id: 'hero-label-right',
          type: 'text_block',
          style: 'micro-label-right',
          content: 'embrace\nthe summer',
          intent: {
            role: 'metaText',
            importance: 'low',
            behavior: 'decorative',
            mobileBehavior: 'hide',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.88, y: 0.72, w: 0.14, z: 3 },
            mobile:  { x: 0.86, y: 0.72, w: 0.20, z: 3 },
          },
        },
      ],
    },

    // ━━━ STORY ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // White editorial: asymmetric photos, typewriter text, torn note, East Asian quote
    {
      id: 'story-collage',
      type: 'collage',
      label: 'Story',
      variant: 'ocean-story',
      sectionStyle: 'editorialStoryCanvas',
      visible: true,
      height: { mobile: 1520, desktop: 1180 },
      background: {
        type: 'color',
        color: '#fbfaf8',
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [
        // ── Top-left photo ──
        {
          id: 'story-photo-left',
          type: 'image_card',
          style: 'plain-photo',
          src: 'https://images.unsplash.com/photo-1522673607200-164d1b6ce486?q=80&w=1200&auto=format&fit=crop',
          rotation: 0,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidText',
            animationPriority: 1,
          },
          layout: {
            desktop: { x: 0.21, y: 0.18, w: 0.27, z: 4 },
            mobile:  { x: 0.46, y: 0.12, w: 0.72, z: 4 },
          },
        },
        // ── Thank you (top-right) ──
        {
          id: 'story-copy-top-right',
          type: 'text_block',
          style: 'typewriter-small-right',
          content: "Thank you so much for\ncelebrating our special day.\nWe hope you had as much\nfun as we did!\n\nLove",
          intent: {
            role: 'bodyText',
            importance: 'medium',
            behavior: 'fixed',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidImportant',
            animationPriority: 2,
          },
          layout: {
            desktop: { x: 0.68, y: 0.12, w: 0.26, z: 8 },
            mobile:  { x: 0.45, y: 0.29, w: 0.74, z: 8 },
          },
        },
        // ── Body copy (left) ──
        {
          id: 'story-copy-left-body',
          type: 'text_block',
          style: 'typewriter',
          content: "Let's party!\n\nThe best things in life\nare meant to be shared.\nThank you for being here\nand celebrating with us today.\nWe are so grateful for\nall the love, support,\nand guidance we've received\nfrom all of you!",
          intent: {
            role: 'bodyText',
            importance: 'medium',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidImportant',
            animationPriority: 3,
          },
          layout: {
            desktop: { x: 0.15, y: 0.47, w: 0.23, z: 8 },
            mobile:  { x: 0.45, y: 0.44, w: 0.74, z: 8 },
          },
        },
        // ── Right large photo ──
        {
          id: 'story-photo-right',
          type: 'image_card',
          style: 'plain-photo-large',
          src: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?q=80&w=1200&auto=format&fit=crop',
          rotation: 0,
          intent: {
            role: 'image',
            importance: 'high',
            behavior: 'flexible',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidText',
            animationPriority: 2,
          },
          layout: {
            desktop: { x: 0.60, y: 0.44, w: 0.33, z: 5 },
            mobile:  { x: 0.47, y: 0.64, w: 0.76, z: 5 },
          },
        },
        // ── Torn handwritten note ──
        {
          id: 'story-note-torn',
          type: 'text_note',
          style: 'torn-note',
          content: "i love you\ni will always love you,\neven with your hands\naround my throat or\nespecially then.",
          rotation: -8,
          intent: {
            role: 'decorative',
            importance: 'medium',
            behavior: 'decorative',
            mobileBehavior: 'preserve',
            overlapPolicy: 'allow',
            animationPriority: 5,
          },
          layout: {
            desktop: { x: 0.18, y: 0.77, w: 0.28, z: 9 },
            mobile:  { x: 0.30, y: 0.84, w: 0.50, z: 9 },
          },
        },
        // ── East Asian quote (right bottom) ──
        {
          id: 'story-quote-east-asian',
          type: 'text_block',
          style: 'editorial-east-asian',
          content: "\u4ece\u6b64\u6211\u4eec\u624b\u62c9\u624b\n\u5411\u7740\u540c\u4e00\u4e2a\u65b9\u5411\u8d70\n\n\u76f4\u5230\u5929\u9ed1\n\u5f85\u751f\u547d\u7ed3\u675f\n\u6211\u4eec\u624d\u7ed3\u675f",
          intent: {
            role: 'bodyText',
            importance: 'medium',
            behavior: 'fixed',
            mobileBehavior: 'preserve',
            overlapPolicy: 'avoidImportant',
            animationPriority: 4,
          },
          layout: {
            desktop: { x: 0.75, y: 0.80, w: 0.18, z: 8 },
            mobile:  { x: 0.65, y: 0.93, w: 0.22, z: 8 },
          },
        },
      ],
    },

    // ━━━ FOOTER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Atmospheric beach image, optional closing text
    {
      id: 'footer-collage',
      type: 'collage',
      label: 'Footer',
      variant: 'ocean-footer',
      sectionStyle: 'footerAtmosphere',
      visible: true,
      height: { mobile: 300, desktop: 420 },
      background: {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1600&auto=format&fit=crop',
        overlay: 'rgba(245,241,235,0.10)',
        grayscale: 0.35,
      },
      editableRules: {
        fields: ['elements'],
        locked: ['background', 'height'],
      },
      data: {},
      elements: [],
    },
  ],
};
